import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Eye, ArrowsClockwise, Trophy, CheckCircle, ChartBar, Percent } from "@phosphor-icons/react";

function StatCard({ icon, label, value, suffix = "", color = "var(--secondary)" }) {
  return (
    <div className="card-pop p-5" style={{ background: color }}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">
        {icon}{label}
      </div>
      <div className="display text-3xl sm:text-4xl font-black mt-2 tracking-tighter">{value}{suffix}</div>
    </div>
  );
}

export default function OutletAnalytics() {
  const { id } = useParams();
  const [stats, setStats] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [outlet, setOutlet] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [o, a, atts] = await Promise.all([
          api.get(`/outlets/${id}`),
          api.get(`/outlets/${id}/analytics`),
          api.get(`/outlets/${id}/attempts`),
        ]);
        setOutlet(o.data);
        setStats(a.data);
        setAttempts(atts.data);
      } catch (e) {
        toast.error("Failed to load analytics");
      }
    })();
  }, [id]);

  if (!stats || !outlet) return <DashboardLayout><div className="text-[var(--ink-muted)]">Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout
      title={`Analytics — ${outlet.name}`}
      actions={
        <Link to={`/outlet/${id}`} className="btn-ghost" data-testid="analytics-back-btn" style={{ padding: "8px 14px", fontSize: 13 }}>
          <ArrowLeft size={16} weight="bold" /> Back
        </Link>
      }
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="analytics-stats-grid">
        <StatCard icon={<Eye size={14} weight="bold" />} label="Total scans" value={stats.total_scans} color="var(--pastel-blue)" />
        <StatCard icon={<ArrowsClockwise size={14} weight="bold" />} label="Spin attempts" value={stats.spin_attempts} color="var(--secondary)" />
        <StatCard icon={<Trophy size={14} weight="bold" />} label="Wins" value={stats.wins} color="var(--pastel-yellow)" />
        <StatCard icon={<CheckCircle size={14} weight="bold" />} label="Redeemed" value={stats.redemptions} color="var(--pastel-purple)" />
      </div>

      <div className="grid sm:grid-cols-3 gap-5 mt-6">
        <StatCard icon={<Percent size={14} weight="bold" />} label="Scan→Spin rate" value={stats.scan_to_spin_rate} suffix="%" />
        <StatCard icon={<Percent size={14} weight="bold" />} label="Win rate" value={stats.win_rate} suffix="%" />
        <StatCard icon={<Percent size={14} weight="bold" />} label="Redemption rate" value={stats.redemption_rate} suffix="%" />
      </div>

      <div className="card-pop p-6 mt-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="display text-lg font-extrabold flex items-center gap-2"><ChartBar size={18} weight="bold" /> Recent attempts</h3>
          <Link to={`/outlet/${id}/redeem`} className="btn-ghost" data-testid="analytics-redeem-link" style={{ padding: "8px 14px", fontSize: 13 }}>
            Open redemption
          </Link>
        </div>
        {attempts.length === 0 ? (
          <div className="text-sm text-[var(--ink-muted)] py-6 text-center">No attempts yet. Share your QR!</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-[var(--ink-muted)] border-b border-[var(--border-soft)]">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Reward</th>
                  <th className="py-2 pr-3">Code</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              </thead>
              <tbody data-testid="analytics-attempts-table">
                {attempts.map((a) => (
                  <tr key={a.id} className="border-b border-[var(--border-soft)] last:border-0">
                    <td className="py-2.5 pr-3 text-[var(--ink-muted)]">{new Date(a.created_at).toLocaleString()}</td>
                    <td className="py-2.5 pr-3 font-semibold">{a.reward_label}</td>
                    <td className="py-2.5 pr-3 code-display text-xs">{a.redemption_code || "—"}</td>
                    <td className="py-2.5 pr-3">
                      {a.reward_type === "no_reward" ? (
                        <span className="tag" style={{ background: "var(--bg)" }}>No win</span>
                      ) : a.redeemed ? (
                        <span className="tag" style={{ background: "var(--success)", color: "#fff", borderColor: "var(--ink)" }}>Redeemed</span>
                      ) : (
                        <span className="tag" style={{ background: "var(--pastel-yellow)" }}>Pending</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
